import { computed, inject } from '@angular/core';
import { signalStore, withState, withMethods, withComputed } from '@ngrx/signals';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import {
  GALLERY_MANAGE_DATA,
  type AddVideoResult,
  type DeleteSceneResult,
  type PhotoDeleteResult,
  type UpdateVideoResult,
} from '../infrastructure/gallery-manage.data';
import {
  GallerySettingsApiService,
  type GalleryStatus,
} from '../../../gallery-settings/domain/infrastructure/gallery-settings-api.service';
import { galleryPhotoSortMode } from '../entities/gallery-manage.models';
import type { ManageScene, ManageScenePhoto, PhotoSortMode } from '../entities/gallery-manage.models';
import type { SyncResult } from '../entities/upload.models';
import { environment } from '@environment';

// 'no-access' — сервер отказал в доступе (403/404): чужая или несуществующая галерея.
// 'error' — любой другой провал чтения, его лечит кнопка повтора.
export type GalleryManageStatus = 'idle' | 'loading' | 'ready' | 'error' | 'no-access';

interface GalleryManageState {
  galleryId: string | null;
  scenes: ManageScene[];
  status: GalleryManageStatus;
  // Cross-scene photo multi-selection, shared by drag / move-copy / delete.
  selectedPhotoIds: string[];
  // The photo id a range extends FROM (shift-click anchor); null resets to the click.
  selectionAnchorId: string | null;
  // Фото, которым readiness-опрос вынес отказ обработки. Сервер этот вердикт не
  // хранит, а сцены перечитываются на каждый чих — без своего списка красная плитка
  // теряется на первой же перечитке.
  failedPhotoIds: string[];
  // Статус ЭТОЙ галереи, прочитанный по ней самой. Список кабинета сюда не годится: по прямой
  // ссылке на галерею со второй страницы списка строки нет вовсе, а пилюля «Выключена» есть.
  galleryStatus: GalleryStatus | null;
  // Сцены-источники кросс-переносов, ждущих ответа сервера: экран притушает их плитки до
  // ответа. Мультимножество — два переноса могут делить один источник, и снятие одного не
  // должно гасить подсветку второго.
  movingSourceSceneIds: string[];
  // Жест перетаскивания идёт: перечитка сцен (опрос готовности, живая шина) ждёт сброса —
  // иначе плитка под курсором уезжает на глазах у фотографа. Флаг ставит страница.
  dragActive: boolean;
}

const initialState: GalleryManageState = {
  galleryId: null,
  scenes: [],
  status: 'idle',
  selectedPhotoIds: [],
  selectionAnchorId: null,
  failedPhotoIds: [],
  galleryStatus: null,
  movingSourceSceneIds: [],
  dragActive: false,
};

// Штамп отказа поверх серверного ответа: сервер отдаёт такую плитку как
// «в обработке», вердикт живёт только здесь.
function stampFailed(scenes: ManageScene[], failedIds: readonly string[]): ManageScene[] {
  if (failedIds.length === 0) return scenes;
  const ids = new Set(failedIds);
  return scenes.map(scene => ({
    ...scene,
    photos: scene.photos.map(photo => (ids.has(photo.id) ? { ...photo, processingStatus: 'failed' as const } : photo)),
  }));
}

// Flat, render-order list of every photo id across the PHOTO scenes — the axis a
// shift-range walks. Video scenes carry no selectable photos.
function orderedPhotoIds(scenes: readonly ManageScene[]): string[] {
  return scenes.filter(scene => scene.kind === 'photo').flatMap(scene => scene.photos.map(photo => photo.id));
}

export const GalleryManageStore = signalStore(
  { providedIn: 'root' },
  environment.storeWithDevTools('galleryManage'),
  withState<GalleryManageState>(initialState),
  withComputed(store => ({
    // Режим сортировки, который показывает шапка галереи: старые режимы сцен
    // сворачиваются к ближайшему из трёх, разнобой — к «Вручную».
    gallerySortMode: computed(() => galleryPhotoSortMode(store.scenes())),
    selectedSet: computed(() => new Set(store.selectedPhotoIds())),
    inFlightSourceSceneIds: computed<ReadonlySet<string>>(() => new Set(store.movingSourceSceneIds())),
    selectedCount: computed(() => store.selectedPhotoIds().length),
    hasSelection: computed(() => store.selectedPhotoIds().length > 0),
    // The selection in cross-scene render order — the block order a group drop lays down.
    orderedSelection: computed(() => {
      const sel = new Set(store.selectedPhotoIds());
      return orderedPhotoIds(store.scenes()).filter(id => sel.has(id));
    }),
    // Активная сцена = сцена якоря выделения (последней тронутой плитки), иначе сцена
    // первого выбранного фото. На неё смотрит меню счётчика «Выбрать все» / «Снять все».
    activeSceneId: computed(() => {
      const marker = store.selectionAnchorId() ?? store.selectedPhotoIds()[0];
      if (!marker) return null;
      return store.scenes().find(scene => scene.photos.some(photo => photo.id === marker))?.id ?? null;
    }),
  })),
  // sceneHiddenChains serializes visibility PUTs per scene: rapid re-toggles
  // otherwise race in flight and can land out of order, leaving the DB opposite
  // to the optimistic UI (a scene meant to stay client-only served publicly).
  // readScenes — ЕДИНСТВЕННАЯ дверь для серверного ответа со сценами: любая перечитка
  // (загрузка, refresh, откат перетаскивания, удаление) проходит через неё и получает
  // штамп отказов обратно. Иначе каждая из них гасит красные плитки.
  withMethods(
    (
      store,
      data = inject(GALLERY_MANAGE_DATA),
      toast = inject(ToastService),
      settingsApi = inject(GallerySettingsApiService),
      sceneHiddenChains = new Map<string, Promise<void>>(),
      // Поколение записи режима сортировки галереи: две быстрые смены подряд
      // приходят вразнобой, и поздний ответ первой затирал бы шапку более свежей.
      sortModeGeneration = { value: 0 },
      // Имена сцен, чья запись ещё в полёте. Перечитка, пришедшая до ответа записи
      // (readyPulse, живая шина), несёт старое имя: без наложения она затирала
      // оптимистичное, и шапка объявляла отказ удавшемуся переименованию.
      pendingSceneNames = new Map<string, string>(),
      // Отказ в доступе дверь помечает сама — так вердикт получают ВСЕ перечитки, а
      // вызывающие сохраняют прежний контракт «сцены или null» (без него пришлось бы
      // разбирать исход в каждом из двух десятков откатов).
      readScenes = async (galleryId: string): Promise<ManageScene[] | null> => {
        const result = await data.scenes(galleryId);
        if (result.ok) {
          const scenes = stampFailed(result.scenes, store.failedPhotoIds());
          if (pendingSceneNames.size === 0) return scenes;
          return scenes.map(scene => {
            const name = pendingSceneNames.get(scene.id);
            return name === undefined ? scene : { ...scene, name };
          });
        }

        if (result.reason === 'no_access' && store.galleryId() === galleryId) {
          updateState(store, '[GalleryManage] Access Denied', { status: 'no-access' });
        }

        return null;
      },
      // Сцена, к которой сервер привяжет видео. Пункт меню называет свою сцену — она и
      // есть якорь. Без неё (входа из шапки больше нет, остаётся пустая галерея) берём
      // ПОСЛЕДНЮЮ пустую фото-сцену: она превращается в видео-сцену на месте и не
      // оставляет сироту; иначе последнюю сцену — к ней видео допишется следом.
      // Очередь сохранений перетаскивания (спека 30–33): на весь экран один серверный
      // раунд-трип (запись + перечитывание) в полёте. Ключ — сцены, которые запись
      // переписывает. Порядок Map = порядок очереди.
      saveQueue = new Map<string, { run: () => Promise<void>; sources: readonly string[] }>(),
      saveDrain = { value: null as Promise<void> | null },
      saveSeq = { value: 0 },
      // Долг перечитки: кросс-перенос, любой отказ или отложенный refresh. Пока очередь не
      // пуста, серверная правда НЕ применяется — она затирала бы более поздние оптимистичные
      // сбросы (плитки прыгали назад); одна перечитка идёт, когда очередь опустела.
      settleRead = { value: false },
      // Подсветка источников — мультимножество: снимаем ровно по одному вхождению.
      holdSources = (ids: readonly string[], hold: boolean): void => {
        if (ids.length === 0) return;
        const next = [...store.movingSourceSceneIds()];
        if (hold) next.push(...ids);
        else
          for (const id of ids) {
            const at = next.indexOf(id);
            if (at >= 0) next.splice(at, 1);
          }

        updateState(store, '[GalleryManage] Moving Sources', { movingSourceSceneIds: next });
      },
      drainSaves = async (): Promise<void> => {
        for (;;) {
          const first = saveQueue.entries().next();
          if (first.done) {
            if (settleRead.value) {
              settleRead.value = false;
              const galleryId = store.galleryId();
              const fresh = galleryId ? await readScenes(galleryId) : null;
              // Новые сбросы за время перечитки: её результат уже устарел — долг остаётся,
              // цикл дойдёт до пустой очереди и перечитает снова.
              if (saveQueue.size > 0) {
                settleRead.value = true;
                continue;
              }

              if (fresh && store.galleryId() === galleryId) {
                updateState(store, '[GalleryManage] Saves Settled', { scenes: fresh });
              }
            }

            // Снимается в том же синхронном шаге, что и проверка пустоты, — иначе
            // постановка между выходом из цикла и сбросом флага повисла бы без запуска.
            saveDrain.value = null;
            return;
          }

          const [key, entry] = first.value;
          saveQueue.delete(key);
          try {
            await entry.run();
          } finally {
            holdSources(entry.sources, false);
          }
        }
      },
      // Замещать можно только запись внутри сцены: она переписывает порядок сцены целиком,
      // поэтому поздняя запись выражает и раннюю. Кросс-перенос не замещается никогда —
      // съеденная запись оставила бы фото на сервере в старой сцене, и перечитывание
      // после успеха вернуло бы плитки назад на глазах у фотографа.
      enqueueSave = (key: string, sources: readonly string[], replaceable: boolean, run: () => Promise<void>): Promise<void> => {
        const queueKey = replaceable ? key : `${key}#${(saveSeq.value += 1)}`;
        const replaced = saveQueue.get(queueKey);
        if (replaced) {
          holdSources(replaced.sources, false);
          // Замещённая запись уходит в хвост: на старом месте она обогнала бы кросс-перенос,
          // вставший позже, и сервер затёр бы его состав старым порядком сцены.
          saveQueue.delete(queueKey);
        }

        holdSources(sources, true);
        saveQueue.set(queueKey, { run, sources });
        saveDrain.value ??= drainSaves();

        return saveDrain.value;
      },
      // Единственная дверь сброса плиток (спека 7–12, 21–33): экран уже посчитал слот,
      // сюда приходит индекс вставки в порядке приёмника БЕЗ пачки.
      runDrop = async (
        photoIds: readonly string[],
        sourceSceneId: string,
        targetSceneId: string,
        targetIndex: number,
      ): Promise<void> => {
        const galleryId = store.galleryId();
        if (!galleryId || photoIds.length === 0) return;
        const scenes = store.scenes();
        const target = scenes.find(scene => scene.id === targetSceneId);
        if (!target) return;
        const payload = new Set(photoIds);
        const byId = new Map(scenes.flatMap(scene => scene.photos.map(photo => [photo.id, photo] as const)));
        const block = photoIds.map(id => byId.get(id)).filter((photo): photo is ManageScenePhoto => !!photo);
        if (block.length === 0) return;

        // Источники берём из самих фото, а не только из объявленной сцены: пачка выделения
        // живёт поперёк сцен, и подсветка с ключом очереди должны знать их все.
        const sourceSceneIds = scenes
          .filter(
            scene =>
              scene.id !== targetSceneId && (scene.id === sourceSceneId || scene.photos.some(photo => payload.has(photo.id))),
          )
          .map(scene => scene.id);
        const cross = sourceSceneIds.length > 0;
        const targetOrderedIds = target.photos.map(photo => photo.id);
        const remaining = target.photos.filter(photo => !payload.has(photo.id));
        const at = Math.max(0, Math.min(targetIndex, remaining.length));
        const photos = [...remaining.slice(0, at), ...block, ...remaining.slice(at)];
        // Холостой сброс: порядок сцены не изменился — ни записи, ни режима, ни тоста.
        // Кросс-перенос холостым быть не может: состав сцен всегда меняется.
        if (!cross && photos.every((photo, index) => photo.id === targetOrderedIds[index])) return;

        // Перекладка — вне очереди, сразу на сбросе; сериализуется только сеть. Режим
        // «Вручную» ставится приёмнику той же оптимистичной записью: сервер делает это
        // одной транзакцией с порядком, отдельного запроса режима у сцены нет.
        const next = scenes.map(scene => {
          if (scene.id === targetSceneId) {
            return { ...scene, photos, photoCount: photos.length, photoSortMode: 'manual' as const };
          }

          const kept = scene.photos.filter(photo => !payload.has(photo.id));
          return kept.length === scene.photos.length ? scene : { ...scene, photos: kept, photoCount: kept.length };
        });
        updateState(store, '[GalleryManage] Drop Photos (optimistic)', { scenes: next });

        const key = cross
          ? [...sourceSceneIds, targetSceneId].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)).join('|')
          : targetSceneId;
        return enqueueSave(key, cross ? sourceSceneIds : [], !cross, async () => {
          const ok = await data.movePhotos(galleryId, photoIds, targetSceneId, targetOrderedIds, at);
          if (store.galleryId() !== galleryId) return;
          // Успех внутри сцены — без тоста и без перечитывания; успех кросс-переноса и
          // любой отказ (откат) перечитывают сцены, когда очередь опустеет.
          if (ok && !cross) return;
          if (!ok) toast.error(cross ? 'Не удалось переместить фото' : 'Не удалось изменить порядок фото');
          settleRead.value = true;
        });
      },
      // Порядок сцен переписывается целиком присланным списком; неназванные сцены
      // остаются хвостом в прежнем порядке.
      runSceneReorder = async (orderedIds: readonly string[]): Promise<void> => {
        const galleryId = store.galleryId();
        if (!galleryId || orderedIds.length === 0) return;
        const scenes = store.scenes();
        const byId = new Map(scenes.map(scene => [scene.id, scene] as const));
        const named = orderedIds.map(id => byId.get(id)).filter((scene): scene is ManageScene => !!scene);
        const namedIds = new Set(named.map(scene => scene.id));
        const next = [...named, ...scenes.filter(scene => !namedIds.has(scene.id))];
        if (next.every((scene, index) => scene.id === scenes[index]?.id)) return;
        updateState(store, '[GalleryManage] Reorder Scenes (optimistic)', { scenes: next });

        return enqueueSave('scenes', [], true, async () => {
          const ok = await data.reorderScenes(
            galleryId,
            next.map(scene => scene.id),
          );
          if (store.galleryId() !== galleryId || ok) return;
          toast.error('Не удалось изменить порядок сцен');
          settleRead.value = true;
        });
      },
      videoAnchor = (requested?: string): string | null => {
        if (requested) return requested;
        const scenes = store.scenes();
        const trailingEmpty = [...scenes].reverse().find(scene => scene.kind === 'photo' && scene.photoCount === 0);
        return trailingEmpty?.id ?? scenes[scenes.length - 1]?.id ?? null;
      },
    ) => ({
      async load(galleryId: string): Promise<void> {
        updateState(store, '[GalleryManage] Load Start', { galleryId, scenes: [], status: 'loading' });
        const scenes = await readScenes(galleryId);
        if (store.galleryId() !== galleryId) return;
        if (scenes) {
          updateState(store, '[GalleryManage] Load Ready', { scenes, status: 'ready' });
        } else if (store.status() !== 'no-access') {
          updateState(store, '[GalleryManage] Load Failed', { status: 'error' });
        }
      },

      // Статус галереи для пилюли в шапке — из настроек самой галереи, одним чтением на вход.
      // Отказ оставляет `null`: пилюли просто нет, экран из-за неё не падает.
      async loadStatus(galleryId: string): Promise<void> {
        const settings = await settingsApi.getSettings(galleryId).catch(() => null);
        if (store.galleryId() !== galleryId) return;
        updateState(store, '[GalleryManage] Status Read', { galleryStatus: settings?.status ?? null });
      },

      // In-place re-read (no loading flip): the grid keeps rendering while fresh
      // thumbnails land during upload readiness polling.
      async refresh(galleryId: string): Promise<void> {
        if (store.galleryId() !== galleryId || store.status() !== 'ready') return;
        // Посреди жеста или сохранения серверная правда затёрла бы оптимистичный порядок —
        // перечитка откладывается до конца очереди сохранений.
        if (store.dragActive() || saveDrain.value) {
          settleRead.value = true;
          return;
        }

        const scenes = await readScenes(galleryId);
        if (store.galleryId() !== galleryId || !scenes) return;
        updateState(store, '[GalleryManage] Refresh', { scenes });
      },

      // Отказ обработки сервер не хранит — вердикт рождается только в ответе
      // readiness-опроса, который страница и так делает один раз за открытие галереи.
      // Раскладываем ЭТОТ ответ по загруженным плиткам и запоминаем отказавшие id:
      // они переживают все последующие перечитки сцен. Ответ поимённо снимает отказ с
      // тех, кто снова жив, 'ready' и неизвестные фото оставляют серверный статус.
      applyProcessingStatuses(galleryId: string, statuses: SyncResult['photos']): void {
        if (store.galleryId() !== galleryId) return;
        const failed = new Set(store.failedPhotoIds());
        for (const [photoId, entry] of Object.entries(statuses)) {
          if (entry.status === 'failed') failed.add(photoId);
          else failed.delete(photoId);
        }

        const failedPhotoIds = [...failed];
        const scenes = store.scenes().map(scene => ({
          ...scene,
          photos: scene.photos.map(photo =>
            statuses[photo.id]?.status === 'pending' && photo.processingStatus !== 'processing'
              ? { ...photo, processingStatus: 'processing' as const }
              : photo,
          ),
        }));
        updateState(store, '[GalleryManage] Processing Statuses', {
          scenes: stampFailed(scenes, failedPhotoIds),
          failedPhotoIds,
        });
      },

      // Сброс пачки перетаскивания — одна дверь для всех жестов экрана. targetIndex —
      // индекс вставки в порядке сцены-приёмника БЕЗ пачки (его считает жест).
      dropPhotos(input: {
        photoIds: string[];
        sourceSceneId: string;
        targetSceneId: string;
        targetIndex: number;
      }): Promise<void> {
        return runDrop(input.photoIds, input.sourceSceneId, input.targetSceneId, input.targetIndex);
      },

      // Страница держит флаг ровно на время жеста: pointerdown/начало → true, сброс/отмена → false.
      setDragActive(active: boolean): void {
        if (store.dragActive() === active) return;
        updateState(store, '[GalleryManage] Drag Active', { dragActive: active });
      },

      // Move the current selection to a scene in THIS gallery (append at its tail),
      // then re-read + clear the selection. Reuses the group move-to-position endpoint.
      async assignSelectionToScene(galleryId: string, targetSceneId: string): Promise<boolean> {
        if (store.galleryId() !== galleryId) return false;
        const ids = orderedPhotoIds(store.scenes()).filter(id => new Set(store.selectedPhotoIds()).has(id));
        if (ids.length === 0) return false;
        const target = store.scenes().find(s => s.id === targetSceneId);
        const targetOrderedIds = target ? target.photos.map(p => p.id) : [];
        const ok = await data.movePhotos(galleryId, ids, targetSceneId, targetOrderedIds, targetOrderedIds.length);
        if (store.galleryId() !== galleryId) return ok;
        const fresh = await readScenes(galleryId);
        if (store.galleryId() === galleryId && fresh) {
          updateState(store, '[GalleryManage] Assign To Scene', { scenes: fresh, selectedPhotoIds: [], selectionAnchorId: null });
        }

        return ok;
      },

      // Move the selection to ANOTHER gallery (lands in its first photo scene); on
      // success the photos leave this gallery, so re-read + clear the selection.
      async moveSelectionToGallery(galleryId: string, targetGalleryId: string): Promise<boolean> {
        if (store.galleryId() !== galleryId) return false;
        const ids = [...store.selectedPhotoIds()];
        if (ids.length === 0) return false;
        const ok = await data.movePhotosToGallery(galleryId, ids, targetGalleryId);
        if (store.galleryId() !== galleryId) return ok;
        const fresh = await readScenes(galleryId);
        if (store.galleryId() === galleryId && fresh) {
          updateState(store, '[GalleryManage] Move To Gallery', { scenes: fresh, selectedPhotoIds: [], selectionAnchorId: null });
        }

        return ok;
      },

      // Delete the current selection. Returns the server's workflow envelope so the
      // caller can branch on cover_warning (confirm then retry with confirmCover) or
      // locked; only a true 'ok' re-reads the scenes + clears the selection.
      async deleteSelection(galleryId: string, confirmCover?: boolean): Promise<PhotoDeleteResult> {
        if (store.galleryId() !== galleryId) return { ok: false, reason: 'error' };
        const ids = [...store.selectedPhotoIds()];
        if (ids.length === 0) return { ok: false, reason: 'error' };
        const result = await data.deletePhotos(galleryId, ids, confirmCover);
        if (store.galleryId() !== galleryId) return result;
        if (result.ok) {
          const fresh = await readScenes(galleryId);
          if (store.galleryId() === galleryId && fresh) {
            updateState(store, '[GalleryManage] Delete Photos', { scenes: fresh, selectedPhotoIds: [], selectionAnchorId: null });
          }
        }

        return result;
      },

      // Одно фото из просмотрщика (s9-ac6): та же серверная дверь и тот же конверт, что у
      // пачки; вьювер листает свой список, но удалённое фото из выделения выпадает.
      async deletePhoto(galleryId: string, photoId: string, confirmCover?: boolean): Promise<PhotoDeleteResult> {
        if (store.galleryId() !== galleryId) return { ok: false, reason: 'error' };
        const result = await data.deletePhotos(galleryId, [photoId], confirmCover);
        if (store.galleryId() !== galleryId) return result;
        if (result.ok) {
          const fresh = await readScenes(galleryId);
          if (store.galleryId() === galleryId && fresh) {
            const selectedPhotoIds = store.selectedPhotoIds().filter(id => id !== photoId);
            const selectionAnchorId = store.selectionAnchorId() === photoId ? null : store.selectionAnchorId();
            updateState(store, '[GalleryManage] Delete Photo', { scenes: fresh, selectedPhotoIds, selectionAnchorId });
          }
        }

        return result;
      },

      // «Повторить» на плитке с ошибкой обработки: сервер ставит фото в конвейер заново,
      // свежий статус (и превью) приходит с перечиткой сцен.
      async reprocessPhoto(galleryId: string, photoId: string): Promise<void> {
        if (store.galleryId() !== galleryId) return;
        const ok = await data.reprocessPhoto(photoId);
        if (!ok) {
          toast.error('Обработка не запустилась — попробуйте ещё раз');
          return;
        }

        if (store.galleryId() !== galleryId) return;
        // Ретрай снимает вердикт отказа — иначе перечитка снова проштампует плитку
        // красной, и фото никогда не дойдёт до «в обработке», а потом до готового.
        const failedPhotoIds = store.failedPhotoIds().filter(id => id !== photoId);
        updateState(store, '[GalleryManage] Reprocess Photo', { failedPhotoIds });
        const fresh = await readScenes(galleryId);
        if (store.galleryId() === galleryId && fresh) {
          updateState(store, '[GalleryManage] Reprocess Photo Reload', { scenes: fresh });
        }
      },

      // Перетаскивание сцены за ручку: экран отдаёт готовый порядок сцен.
      reorderScenes(sceneIds: string[]): Promise<void> {
        return runSceneReorder(sceneIds);
      },

      // Create then re-read: the server assigns the sort order and (optionally) the
      // positional auto-name, so the full scene comes back via a fresh scenes read
      // rather than being synthesised client-side. Returns the new scene id.
      async addScene(galleryId: string, name?: string): Promise<string | null> {
        if (store.galleryId() !== galleryId) return null;
        const id = await data.addScene(galleryId, name);
        if (store.galleryId() !== galleryId) return id;
        if (id) {
          const fresh = await readScenes(galleryId);
          if (store.galleryId() === galleryId && fresh) {
            updateState(store, '[GalleryManage] Add Scene', { scenes: fresh });
          }
        }

        return id;
      },

      // Embed a YouTube/Vimeo video into the scene the menu was opened from (videoAnchor);
      // with no scenes at all, create one first. Re-read after — the server assigns sort
      // order + resolves the provider embed URL.
      async addVideo(galleryId: string, url: string, name?: string, sceneId?: string): Promise<AddVideoResult> {
        if (store.galleryId() !== galleryId) return { ok: false, reason: 'unknown' };
        const anchorSceneId = videoAnchor(sceneId) ?? (await data.addScene(galleryId));
        if (store.galleryId() !== galleryId) return { ok: false, reason: 'unknown' };
        if (!anchorSceneId) return { ok: false, reason: 'unknown' };

        const result = await data.addVideo(galleryId, anchorSceneId, url, name);
        if (store.galleryId() !== galleryId) return result;
        if (result.ok) {
          const fresh = await readScenes(galleryId);
          if (store.galleryId() === galleryId && fresh) {
            updateState(store, '[GalleryManage] Add Video', { scenes: fresh });
          }
        }

        return result;
      },

      // Turn an ALREADY-uploaded native video file (its public URL) into a video scene.
      // Same anchor rule as addVideo; re-read after so the server-assigned order lands.
      async addVideoFile(
        galleryId: string,
        fileUrl: string,
        fileId?: string,
        name?: string,
        sceneId?: string,
      ): Promise<AddVideoResult> {
        if (store.galleryId() !== galleryId) return { ok: false, reason: 'unknown' };
        const anchorSceneId = videoAnchor(sceneId) ?? (await data.addScene(galleryId));
        if (store.galleryId() !== galleryId) return { ok: false, reason: 'unknown' };
        if (!anchorSceneId) return { ok: false, reason: 'unknown' };

        const result = await data.addVideoFile(galleryId, anchorSceneId, fileUrl, fileId, name);
        if (store.galleryId() !== galleryId) return result;
        if (result.ok) {
          const fresh = await readScenes(galleryId);
          if (store.galleryId() === galleryId && fresh) {
            updateState(store, '[GalleryManage] Add Video File', { scenes: fresh });
          }
        }

        return result;
      },

      // «Редактировать видео» (spec 39): the link and the name save as ONE server call.
      // A chosen poster rides the same save — applied to the video id the server answers
      // with, because a changed link retires the old row; posterPhotoId left out means the
      // poster was not touched. Re-read after, the scene name and embed come from the server.
      async updateVideo(
        galleryId: string,
        sceneId: string,
        videoId: string,
        url: string,
        name: string,
        posterPhotoId?: string | null,
      ): Promise<UpdateVideoResult> {
        if (store.galleryId() !== galleryId) return { ok: false, reason: 'unknown' };
        const result = await data.updateVideo(sceneId, url, name);
        if (store.galleryId() !== galleryId || !result.ok) return result;

        if (posterPhotoId !== undefined) {
          await data.setVideoPoster(result.videoId || videoId, posterPhotoId);
          if (store.galleryId() !== galleryId) return result;
        }

        const fresh = await readScenes(galleryId);
        if (store.galleryId() === galleryId && fresh) {
          updateState(store, '[GalleryManage] Update Video', { scenes: fresh });
        }

        return result;
      },

      async renameScene(galleryId: string, sceneId: string, name: string): Promise<void> {
        if (store.galleryId() !== galleryId) return;
        const next = store.scenes().map(scene => (scene.id === sceneId ? { ...scene, name } : scene));
        updateState(store, '[GalleryManage] Rename Scene (optimistic)', { scenes: next });

        pendingSceneNames.set(sceneId, name);
        const ok = await data.renameScene(sceneId, name);
        // Более новое переименование той же сцены держит своё имя.
        if (pendingSceneNames.get(sceneId) === name) pendingSceneNames.delete(sceneId);
        if (store.galleryId() !== galleryId) return;
        if (!ok) {
          const fresh = await readScenes(galleryId);
          if (store.galleryId() !== galleryId || !fresh) return;
          updateState(store, '[GalleryManage] Rename Scene Rollback', { scenes: fresh });
        }
      },

      // Client-only visibility (spec 04): the hidden scene stays fully manageable
      // here; publicly it is served to the client tier only.
      async setSceneHidden(galleryId: string, sceneId: string, isHidden: boolean): Promise<void> {
        if (store.galleryId() !== galleryId) return;
        const next = store.scenes().map(scene => (scene.id === sceneId ? { ...scene, isHidden } : scene));
        updateState(store, '[GalleryManage] Set Scene Hidden (optimistic)', { scenes: next });

        // Chained per scene so writes reach the server in click order; only the
        // NEWEST link may roll back (an older failure must not clobber the state
        // a newer toggle already painted).
        const run = async (): Promise<void> => {
          const ok = await data.setSceneHidden(sceneId, isHidden);
          if (store.galleryId() !== galleryId) return;
          if (sceneHiddenChains.get(sceneId) !== chained) return;
          if (!ok) {
            const fresh = await readScenes(galleryId);
            if (store.galleryId() !== galleryId || !fresh) return;
            if (sceneHiddenChains.get(sceneId) !== chained) return;
            updateState(store, '[GalleryManage] Set Scene Hidden Rollback', { scenes: fresh });
          }
        };

        const chained: Promise<void> = (sceneHiddenChains.get(sceneId) ?? Promise.resolve()).then(run, run);
        sceneHiddenChains.set(sceneId, chained);
        await chained;
      },

      // Один режим на всю галерею (шапка): одним запросом сервер проставляет его
      // каждой сцене и переупорядочивает фото, поэтому после записи — свежее чтение.
      // Провал: то же чтение возвращает серверную правду, вызывающий показывает тост.
      async setGallerySortMode(galleryId: string, sortMode: PhotoSortMode): Promise<boolean> {
        if (store.galleryId() !== galleryId) return false;
        const generation = (sortModeGeneration.value += 1);
        const optimistic = store.scenes().map(scene => (scene.kind === 'photo' ? { ...scene, photoSortMode: sortMode } : scene));
        updateState(store, '[GalleryManage] Set Gallery Sort Mode (optimistic)', { scenes: optimistic });

        const ok = await data.setGallerySortMode(galleryId, sortMode);
        if (store.galleryId() !== galleryId || sortModeGeneration.value !== generation) return ok;
        const fresh = await readScenes(galleryId);
        if (store.galleryId() !== galleryId || sortModeGeneration.value !== generation || !fresh) return ok;
        updateState(store, ok ? '[GalleryManage] Set Gallery Sort Mode' : '[GalleryManage] Set Gallery Sort Mode Rollback', {
          scenes: fresh,
        });

        return ok;
      },

      // Optimistic remove; a plain failure OR a 409-locked refusal re-reads server
      // truth (restoring the scene) and hands the locked count back to the caller.
      async deleteScene(galleryId: string, sceneId: string): Promise<DeleteSceneResult> {
        if (store.galleryId() !== galleryId) return { ok: false };
        const previous = store.scenes();
        const next = previous.filter(scene => scene.id !== sceneId);
        updateState(store, '[GalleryManage] Delete Scene (optimistic)', { scenes: next });

        const result = await data.deleteScene(sceneId);
        if (store.galleryId() !== galleryId) return result;
        if (!result.ok) {
          const fresh = await readScenes(galleryId);
          updateState(store, '[GalleryManage] Delete Scene Rollback', {
            scenes: store.galleryId() === galleryId && fresh ? fresh : previous,
          });
        }

        return result;
      },

      async setSceneDescription(galleryId: string, sceneId: string, description: string): Promise<void> {
        if (store.galleryId() !== galleryId) return;
        const next = store.scenes().map(scene => (scene.id === sceneId ? { ...scene, description } : scene));
        updateState(store, '[GalleryManage] Set Scene Description (optimistic)', { scenes: next });

        const ok = await data.setSceneDescription(sceneId, description);
        if (store.galleryId() !== galleryId) return;
        if (!ok) {
          const fresh = await readScenes(galleryId);
          if (store.galleryId() !== galleryId || !fresh) return;
          updateState(store, '[GalleryManage] Set Scene Description Rollback', { scenes: fresh });
        }
      },

      async clearSceneDescription(galleryId: string, sceneId: string): Promise<void> {
        if (store.galleryId() !== galleryId) return;
        const next = store.scenes().map(scene => (scene.id === sceneId ? { ...scene, description: null } : scene));
        updateState(store, '[GalleryManage] Clear Scene Description (optimistic)', { scenes: next });

        const ok = await data.clearSceneDescription(sceneId);
        if (store.galleryId() !== galleryId) return;
        if (!ok) {
          const fresh = await readScenes(galleryId);
          if (store.galleryId() !== galleryId || !fresh) return;
          updateState(store, '[GalleryManage] Clear Scene Description Rollback', { scenes: fresh });
        }
      },

      // ── Photo multi-selection (shared by drag / move-copy / delete) ──
      // Plain click on a photo: toggle it and re-anchor the range to it.
      togglePhoto(photoId: string): void {
        const current = store.selectedPhotoIds();
        const next = current.includes(photoId) ? current.filter(id => id !== photoId) : [...current, photoId];
        updateState(store, '[GalleryManage] Toggle Photo', {
          selectedPhotoIds: next,
          selectionAnchorId: photoId,
        });
      },

      // Shift-click: select the contiguous run between the anchor and this photo along
      // the flat render order, replacing the selection (Finder-style). No anchor yet →
      // behaves like a single pick.
      // `order` is the axis the RANGE walks. The screen may render a subset of the store
      // (face filter), and a range taken over store order would then reach photos the
      // photographer cannot see — the caller hands the order it actually rendered.
      selectRangeTo(photoId: string, order: readonly string[] = orderedPhotoIds(store.scenes())): void {
        const anchor = store.selectionAnchorId() ?? photoId;
        const from = order.indexOf(anchor);
        const to = order.indexOf(photoId);
        if (from === -1 || to === -1) {
          updateState(store, '[GalleryManage] Select Range (fallback)', {
            selectedPhotoIds: [photoId],
            selectionAnchorId: photoId,
          });
          return;
        }

        const [lo, hi] = from <= to ? [from, to] : [to, from];
        updateState(store, '[GalleryManage] Select Range', {
          selectedPhotoIds: order.slice(lo, hi + 1),
          selectionAnchorId: anchor,
        });
      },

      selectAllPhotos(): void {
        const all = orderedPhotoIds(store.scenes());
        updateState(store, '[GalleryManage] Select All', {
          selectedPhotoIds: all,
          selectionAnchorId: all[0] ?? null,
        });
      },

      // «Выбрать все» / «Снять все» из меню счётчика — они работают с АКТИВНОЙ сценой,
      // а не со всей галереей (⌘A — вот он про всю галерею).
      selectScenePhotos(sceneId: string, select: boolean): void {
        const scene = store.scenes().find(item => item.id === sceneId);
        if (!scene) return;
        const ids = scene.photos.map(photo => photo.id);
        const current = store.selectedPhotoIds();
        const next = select ? [...current.filter(id => !ids.includes(id)), ...ids] : current.filter(id => !ids.includes(id));
        updateState(store, '[GalleryManage] Select Scene Photos', {
          selectedPhotoIds: next,
          selectionAnchorId: select ? (ids[0] ?? store.selectionAnchorId()) : null,
        });
      },

      clearSelection(): void {
        if (store.selectedPhotoIds().length === 0) return;
        updateState(store, '[GalleryManage] Clear Selection', {
          selectedPhotoIds: [],
          selectionAnchorId: null,
        });
      },
    }),
  ),
);
